import { create } from 'zustand';
import type {
  AuthRuleName,
  AuthRuleSettings,
  User,
} from 'containers/User/user.types';


const defaultPermissions: AuthRuleSettings = {
  read: false,
  write: false,
  approve: false,
};

const defaultPermissionsByRule: Record<AuthRuleName, AuthRuleSettings> = {
  checksheets: defaultPermissions,
  dashboards: defaultPermissions,
  devices: defaultPermissions,
  employees: defaultPermissions,
  entities: defaultPermissions,
  indirects: defaultPermissions,
  instructions: defaultPermissions,
  integrations: defaultPermissions,
  jobs: defaultPermissions,
  organizations: defaultPermissions,
  parts: defaultPermissions,
  programs: defaultPermissions,
  resources: defaultPermissions,
  scheduledTasks: defaultPermissions,
  security: defaultPermissions,
  service: defaultPermissions,
  utilities: defaultPermissions,
};

function makeInitialUser(): User {
  return {
    darkMode: 0,
    globalAdmin: 0,
    enabled: 0,
    restrictOrgs: 0,
    entityId: 0,
    selectedEntityId: 0,
    selectedOrganizationId: 0,
    userId: 0,
    lastUpdated: new Date(),
    permissions: { ...defaultPermissionsByRule },
    country: '',
    timezone: '',
    userName: '',
  };
}

export const INITIAL_USER: User = makeInitialUser();

type UserSelection = Pick<
  User,
  'selectedEntityId' | 'selectedOrganizationId'
>;

interface SessionStoreState {
  user: User;
  setUser: (user: User) => void;
  markUserUpdated: (selection: UserSelection) => void;
  resetUser: () => void;
}

export const useSessionStore = create<SessionStoreState>((set) => ({
  user: makeInitialUser(),
  setUser: (user) => set({ user }),
  markUserUpdated: (selection) =>
    set((state) => ({
      user: { ...state.user, ...selection, lastUpdated: new Date() },
    })),
  resetUser: () => set({ user: makeInitialUser() }),
}));

export const setUser = (user: User) => useSessionStore.getState().setUser(user);
export const markUserUpdated = (selection: UserSelection) =>
  useSessionStore.getState().markUserUpdated(selection);
export const resetUser = () => useSessionStore.getState().resetUser();

export const getUser = () => useSessionStore.getState().user;

export const useUser = () => useSessionStore((s) => s.user);
export const useIsUserDataFetched = () =>
  useSessionStore(
    (s) => s.user.selectedEntityId !== 0 && s.user.selectedOrganizationId !== 0
  );
